// src/prototype/data/adapters/useCaseContentMock.ts
// ============================================================
// USE-CASE CONTENT ADAPTER — MOCK ROW SOURCE
//
// Returns BigQuery-SHAPED rows for one use case.
//
// The 10 FEATURED opportunities (opp-1..opp-10) are anchored to REAL dva-stage /
// client_id '300' data, served from stage300FeaturedContent.ts (composite/sub-scores/ROI/
// risks/milestones/alignment). Everything else derives its frame from its DecisionBrief.
//
// WAVE 1 (spec docs/DVA_Demo_Dataset_Generation_Spec_v1.md v1.1) changed what sits UNDER
// that frame. The per-measure layer is now tiered:
//   Tier A/B — a GENERATED fixture (src/prototype/data/fixtures/generated) carrying real
//              warehouse rationale text, scores and provenance.
//   Tier C   — NOTHING. No measure rows are fabricated. The old genMeasures path invented
//              12 measures and 12 evidence items per uncovered use case; it is deleted.
//              ScoringRationaleTab renders the truthful summary line instead.
//
// This module still fabricates the surrounding frame (alignment, requirements, milestones,
// ROI) for non-featured use cases. To go live, replace getContentRows with a fetch that
// returns the same BQ row shapes — the adapter and UI never change.
// ============================================================

import type { DecisionBrief } from '../decisionBrief';
import {
  DEMO_CLIENT_ID,
  type UseCaseContentRows,
  type MeasureResultRow,
  type MeasureRationalizationRow,
  type UcRiskRow,
  type UcImplMilestoneRow,
  type UcImplRequirementRow,
  type UcAlignmentItemRow,
  type UcRoiOutputsRow,
  type DimensionId,
} from './useCaseContentTypes';
import { OPP_TO_UC, buildFeaturedRows } from './stage300FeaturedContent';
import { generatedFor, buildGeneratedRows } from './generatedRationaleRows';
import { roiForUseCase } from '../../roi/useCaseRoi';

/** $M with one decimal — matches the engine's own range formatting. */
const fmtM = (value: number) => `$${(value / 1e6).toFixed(1)}M`;

// ---- id mapping: frontend opp-id → stage use_case_id -------------------------
// The 10 featured opps map to real client-300 use cases (OPP_TO_UC); every other opp id
// derives its use_case_id from the numeric suffix.
const useCaseIdFor = (id: string): number => OPP_TO_UC[id] ?? (parseInt(id.replace(/\D/g, ''), 10) || 0);

// map demo 0–100 scores onto the BQ scale (matches the v4 design's derivation)
const bq = (v: number, f: number) => Math.round(v * f * 10) / 10;

// =============================================================================
// Generic frame for every non-featured use case (from its DecisionBrief).
// =============================================================================

// category added to each risk (v4 RISK_CAT map)
const RISK_CAT: Record<string, string> = {
  'ADT feed latency': 'Data & Integration', 'Care team adoption': 'Adoption & Change', 'Attribution model disputes': 'Financial', 'Actuarial model validation': 'Financial', 'Contract data completeness': 'Data & Integration', 'Timing vs. renewal calendar': 'Timing', 'Union scheduling rules': 'Regulatory & Policy', 'Forecast trust': 'Adoption & Change', 'Acuity data quality': 'Data & Integration', 'Clearinghouse access': 'Data & Integration', 'Workqueue integration': 'Data & Integration', 'Model drift on payer rules': 'Model & Methodology', 'Measure data completeness': 'Data & Integration', 'Definition disputes': 'Governance', 'Regulatory changes': 'Regulatory & Policy', 'Clinical governance pace': 'Governance', 'Physician acceptance': 'Adoption & Change', 'Outcome adjustment validity': 'Model & Methodology', 'Infrastructure prerequisite': 'Data & Integration', 'Consent framework': 'Regulatory & Policy', 'Specialist recruitment': 'Talent', 'Sensor coverage gap': 'Data & Integration', 'OT/IT integration': 'Data & Integration', 'Savings attribution': 'Model & Methodology', 'Engagement assumptions': 'Adoption & Change', 'Digital divide': 'Equity & Access', 'App fatigue': 'Adoption & Change', 'De-identification maturity': 'Regulatory & Policy', 'Reputational exposure': 'Reputational', 'Market pricing volatility': 'Market',
};


// =============================================================================
// Public: BQ-shaped rows for one use case
// =============================================================================

export function getContentRows(brief: DecisionBrief): UseCaseContentRows {
  const uc = useCaseIdFor(brief.id);
  const risks: UcRiskRow[] = brief.risks.map((r, i) => ({
    client_id: DEMO_CLIENT_ID, use_case_id: uc, risk_id: `${brief.id}-r${i + 1}`,
    name: r.name, category: RISK_CAT[r.name] ?? 'Operational', severity: r.severity,
    likelihood: r.likelihood, mitigation: r.mitigation, owner: r.owner, description: r.description,
  }));

  // Featured (opp-1..opp-10) → real dva-stage / client 300 content.
  if (OPP_TO_UC[brief.id] !== undefined) {
    return buildFeaturedRows(uc, brief.rank);
  }

  // ---- generic derivation ----
  const dimBase: Record<DimensionId, number> = {
    financial: bq(brief.valueScore, 0.83), urgency: bq(brief.valueScore, 0.74),
    strategic: bq(brief.valueScore, 0.88), feasibility: bq(brief.implScore, 0.86),
  };
  const total = Math.round((dimBase.financial * 0.35 + dimBase.urgency * 0.15 + dimBase.strategic * 0.2 + dimBase.feasibility * 0.3) * 10) / 10;
  const conf = Math.round((0.35 + brief.implScore / 250) * 100) / 100;

  // WAVE 1 tiering (spec §5). A generated fixture exists → serve its real per-measure layer
  // (Tier A ranks 11–15, Tier B ranks 16+). No fixture → Tier C: NO fabricated measure rows at
  // all. The old genMeasures path invented 12 measures with invented evidence for every
  // uncovered use case; Tier C now renders dimension scores plus one truthful line instead.
  const gen = generatedFor(brief.id);
  const genBuilt = gen ? buildGeneratedRows(gen, DEMO_CLIENT_ID, uc) : null;
  const measures: MeasureResultRow[] = genBuilt?.measures ?? [];
  const rats: MeasureRationalizationRow[] = genBuilt?.rationalizations ?? [];

  const alignment: UcAlignmentItemRow[] = [
    { client_id: DEMO_CLIENT_ID, use_case_id: uc, priority: 'Stated strategic priority match', score: Math.min(95, brief.valueScore + 5), narrative: 'Aligned with priorities captured in your strategy survey and executive interviews.', method_note: 'Survey rank match' },
    { client_id: DEMO_CLIENT_ID, use_case_id: uc, priority: 'Planning document references', score: Math.max(45, brief.valueScore - 4), narrative: 'Referenced in strategic planning documents reviewed during personalization.', method_note: 'Document reference' },
    { client_id: DEMO_CLIENT_ID, use_case_id: uc, priority: 'Portfolio complementarity', score: Math.max(40, brief.valueScore - 10), narrative: 'Complements adjacent initiatives already in your opportunity portfolio.', method_note: 'P4 portfolio analysis' },
  ];

  const requirements: UcImplRequirementRow[] = [
    { client_id: DEMO_CLIENT_ID, use_case_id: uc, req_type: 'data', name: 'Source data availability confirmed', details: 'Primary data domains verified during the readiness assessment phase.' },
    { client_id: DEMO_CLIENT_ID, use_case_id: uc, req_type: 'technology', name: 'Integration into existing workflow tooling', details: 'Delivery lands inside current systems — no net-new user-facing platform.' },
    { client_id: DEMO_CLIENT_ID, use_case_id: uc, req_type: 'people', name: 'Named delivery capacity', details: 'Analytics and operational FTE allocation confirmed against the roadmap.' },
    { client_id: DEMO_CLIENT_ID, use_case_id: uc, req_type: 'governance', name: 'Executive steering cadence', details: 'Sponsor-chaired steering committee through pilot and scale phases.' },
  ];

  let cum = 0;
  const milestones: UcImplMilestoneRow[] = brief.roadmap.map((p, i) => {
    cum += [1.5, 3.5, 4][Math.min(i, 2)];
    return {
      client_id: DEMO_CLIENT_ID, use_case_id: uc,
      label: `${p.phase} — ${p.desc.replace(/\.$/, '')}`, months_from_start: cum,
      value_note: i === brief.roadmap.length - 1 ? 'Full run-rate value begins' : i === 0 ? 'Unblocks downstream phases' : 'First measurable value signal',
      depends_on: i === 0 ? null : brief.roadmap[i - 1].phase,
    };
  });

  // ROI OUTPUTS COME FROM THE ENGINE. This block previously ran its own model —
  // implementation cost as 14% of the annual midpoint, operating at 4.5%, payback
  // as a flat 5 or 9 by effort tag, and NPV as midpoint x 1.85 — none of which
  // matched the ROI Model page, the Command Center, or the approval PDFs.
  const engine = roiForUseCase(brief.id);
  const engineBase = engine.scenarios.base;
  const roiOutputs: UcRoiOutputsRow = {
    client_id: DEMO_CLIENT_ID, use_case_id: uc,
    annual_benefit_display: `${fmtM(engine.annualValueRangeLow)} – ${fmtM(engine.annualValueRangeHigh)}`,
    annual_benefit: +(engineBase.annualValueMid / 1e6).toFixed(1),
    implementation_cost: +(engineBase.implementationCost / 1e6).toFixed(1),
    operating_cost: +(engineBase.operatingCostPerYear / 1e6).toFixed(2),
    payback_months: engineBase.paybackMonths ?? 0,
    npv_3yr: +(engineBase.netValue3Yr / 1e6).toFixed(1),
    calc_method: `Discounted cash flow · ${Math.round(engine.config.discountRate * 100)}% rate · ${engine.config.horizonYears}-yr horizon (ROI engine)`,
    confidence: engineBase.confidencePct / 100,
  };

  return {
    score: { client_id: DEMO_CLIENT_ID, use_case_id: uc, value_score: brief.valueScore, implementation_score: brief.implScore, total_score: total, rank: brief.rank, confidence_level: conf, financial_composite: dimBase.financial, mode: 'axis_scoring', scoring_run_id: 'run_2026-07-18_0412' },
    // dimScores keeps the four bars painted for Tier C, which has no measures to aggregate.
    dimScores: dimBase,
    measures, rationalizations: rats,
    alignment, requirements, milestones, risks,
    roiOutputs,
    provenance: genBuilt?.provenance,
    personalizationDelta: genBuilt?.personalizationDelta,
  };
}
